import type { ReactNode } from 'react';
import type { RenderedDocument, RenderedSection } from '@oscom/shared';
import { ContentBlockView } from './ContentBlockView';
import { GeneratedContentView } from './GeneratedContentView';

function anchor(section: RenderedSection): string {
  return `kapitel-${section.number || section.id}`.replace(/[^A-Za-z0-9_.-]/g, '-');
}

function TocList({ sections }: { sections: readonly RenderedSection[] }): ReactNode {
  if (sections.length === 0) return null;
  return (
    <ol className="doc-toc__list">
      {sections.map((section) => (
        <li key={section.id}>
          <a href={`#${anchor(section)}`}>
            {section.number} {section.title}
          </a>
          <TocList sections={section.children} />
        </li>
      ))}
    </ol>
  );
}

function PreviewSection({
  section,
  customerId,
  imageUrl,
}: {
  section: RenderedSection;
  customerId: string;
  imageUrl: (assetId: string) => string;
}): ReactNode {
  const Heading = `h${Math.min(section.level + 1, 6)}` as 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  const blocks = section.blocks.map((block) => (
    <ContentBlockView key={block.id} block={block} imageUrl={imageUrl} />
  ));
  const generated =
    section.items.length > 0 ? (
      <GeneratedContentView items={section.items} customerId={customerId} links={false} />
    ) : null;
  return (
    <section className={`doc-section doc-section--level${section.level}`} id={anchor(section)}>
      <Heading>
        {section.number} {section.title}
      </Heading>
      {section.blocksPlacement === 'after' ? (
        <>
          {generated}
          {blocks}
        </>
      ) : (
        <>
          {blocks}
          {generated}
        </>
      )}
      {section.children.map((child) => (
        <PreviewSection
          key={child.id}
          section={child}
          customerId={customerId}
          imageUrl={imageUrl}
        />
      ))}
    </section>
  );
}

/** The whole document as a reading view - also what readers see and what is printed. */
export function DocumentPreview({
  customerId,
  rendered,
  imageUrl,
}: {
  customerId: string;
  rendered: RenderedDocument;
  imageUrl: (assetId: string) => string;
}): ReactNode {
  return (
    <article className="doc-preview">
      <header className="doc-preview__head">
        <p className="muted">
          {rendered.documentTypeName}
          {rendered.siteLabel ? ` · ${rendered.siteLabel}` : ''}
        </p>
        <h1>{rendered.title}</h1>
        {rendered.subtitle ? <p className="doc-preview__subtitle">{rendered.subtitle}</p> : null}
      </header>
      <nav className="doc-toc" aria-label="Inhaltsverzeichnis">
        <h2>Inhalt</h2>
        <TocList sections={rendered.sections} />
      </nav>
      {rendered.sections.map((section) => (
        <PreviewSection
          key={section.id}
          section={section}
          customerId={customerId}
          imageUrl={imageUrl}
        />
      ))}
    </article>
  );
}
